/**
 * THE OUTFORM SYSTEM — container view.
 *
 * Erez, 30 Aug 2026: "I want to see the architecture of the system. Where is
 * the logic? Where is the business logic? The database, a few components of the
 * backend, and then the frontend."
 *
 * REDRAWN 2 Sept 2026, after a nine-cycle audit measured the whole system. The
 * v2 picture had gone stale in one important way: it showed `families/` as part
 * of the engine, and `families/` has been DELETED — 1,651 lines, and the Excel
 * coupling with them. It also showed one engine where there are two METHODS.
 *
 * ARCHETYPE: container view (C4 L2, skill://diagrams §4) — actors bare outside
 * the boundary, [technology] in brackets, protocol on every wire.
 *
 * ⭐ THE ARGUMENT THE PICTURE MAKES NOW. Two red things, and they are the two
 * the audit found already right: the PURE KERNEL, which is 1,442 lines with
 * zero I/O imports, and the WRITE GATE, which is the single door a fact can
 * change through. Everything else moves data or shows it.
 *
 * ⛔ AND THE SECOND ARGUMENT, which is why the dashed boxes are here: every
 * flaw the audit found is a MISSING WIRE OR A MISSING SURFACE, never a wrong
 * shape. The repository layer, the review screen and the price write-back are
 * drawn as absent rather than left out — a diagram that omits what is missing
 * describes a system that has no gaps.
 *
 * Built on the house engine (./c4): nothing has a hand-written width.
 */
import { Canvas, leaf, box, person, type Diagram } from "./c4";

const HEAD: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
  fontSize: 11, letterSpacing: ".14em", color: "#5B6B7C",
  textTransform: "uppercase", marginBottom: 10,
};

const SYS: Diagram = (() => {
  // ── FRONT END ─────────────────────────────────────────────────────────────
  const frontend = box("os-fe", "CONSOLE", "col", [
    leaf("os-shell", "Shell + Rail", "[React]", "PanelTop"),
    leaf("os-bp", "Ballpark input", "[job + BOM]", "ClipboardList"),
    leaf("os-det", "Detailed input", "[9 moves]", "Rows3"),
    leaf("os-frame", "Document frame", "[empty sandbox]", "SquareDashed"),
    leaf("os-chat", "Agent panel", "[omnigent]", "MessageSquare"),
    leaf("os-rev", "Review screen", "[NOT BUILT]", "Inbox"),
  ], { depth: 1, gap: 10, pad: 14 });

  // ── BACK END ──────────────────────────────────────────────────────────────
  const ingest = box("os-ing", "INGEST", "col", [
    leaf("os-load", "raw loader", "[openpyxl]", "Download"),
    leaf("os-adapt", "adapters", "[4 layouts]", "Puzzle"),
    leaf("os-prom", "promote", "[facts]", "ArrowUpFromLine"),
  ], { depth: 2, gap: 8, pad: 11 });

  const engine = box("os-eng", "KERNEL · pure, zero I/O", "col", [
    leaf("os-est", "estimate.py", "[4-tier stack]", "Calculator", "gate"),
    leaf("os-cx", "complexity.py", "[ROUND_HALF_UP]", "Gauge", "gate"),
    leaf("os-pol", "policy.py", "[3-state gates]", "ShieldCheck", "gate"),
    leaf("os-lab2", "labor · freight", "[PF&D x1.2]", "Clock", "gate"),
  ], { depth: 2, gap: 8, pad: 11 });

  const analog = box("os-an", "ANALOGOUS METHOD", "col", [
    leaf("os-engines", "engines.py", "[comparables]", "Search"),
    leaf("os-load2", "loader", "[NOT SPLIT]", "SplitSquareHorizontal"),
  ], { depth: 2, gap: 8, pad: 11 });

  const surface = box("os-surf", "SURFACE", "col", [
    leaf("os-api", "api.py", "[FastAPI, 73]", "Server"),
    leaf("os-wire", "response models", "[4 routes]", "FileJson"),
    leaf("os-tools", "tools.py", "[47 MCP]", "Wrench"),
    leaf("os-views", "views.py", "[renders only]", "FileText"),
  ], { depth: 2, gap: 8, pad: 11 });

  const gate = box("os-gate", "WRITE GATE", "col", [
    leaf("os-writes", "writes.py", "[one door]", "DoorClosed", "gate"),
    leaf("os-ovr", "overrides", "[never mutates]", "Replace", "gate"),
  ], { depth: 2, gap: 8, pad: 11 });

  const repo = box("os-repo", "REPOSITORIES", "col", [
    leaf("os-norepo", "does not exist", "[15 modules own SQL]", "TriangleAlert"),
  ], { depth: 2, gap: 8, pad: 11 });

  const backend = box("os-be", "BACKEND · python", "col", [ingest, engine, analog, surface, gate, repo],
    { depth: 1, gap: 15, pad: 15 });

  // ── DATABASE ──────────────────────────────────────────────────────────────
  const dbRaw = box("os-dbraw", "raw · the receipt", "col", [
    leaf("os-run", "load_run", "[sha256]", "FileCheck"),
    leaf("os-cell", "cell", "[1.0M rows]", "Grid3x3"),
  ], { depth: 2, gap: 8, pad: 11 });

  const dbFacts = box("os-dbf", "facts · append-only", "col", [
    leaf("os-bom", "bom_line", "[398]", "Package"),
    leaf("os-lab", "labor_task", "[44]", "Clock"),
    leaf("os-oth", "other_cost", "[394]", "Boxes"),
    leaf("os-inp", "job_input", "[221]", "SlidersHorizontal"),
  ], { depth: 2, gap: 7, pad: 11 });

  const dbPolicy = box("os-dbp", "policy · per-job copies", "col", [
    leaf("os-rate", "rate_card", "[330, all job-scoped]", "Percent"),
    leaf("os-cf", "complexity_factor", "[15, one job]", "Gauge"),
    leaf("os-pv", "pv_bands", "[4, TENANT]", "Layers"),
  ], { depth: 2, gap: 8, pad: 11 });

  const dbLedger = box("os-dbl", "ledger", "col", [
    leaf("os-estt", "estimates", "[our_cost 5/49]", "Receipt"),
    leaf("os-lines", "estimate_lines", "[764]", "ListTree"),
    leaf("os-fe2", "field_events", "[115 unverified]", "History"),
  ], { depth: 2, gap: 8, pad: 11 });

  const db = box("os-db", "POSTGRES", "col", [dbRaw, dbFacts, dbPolicy, dbLedger],
    { depth: 1, gap: 16, pad: 15 });

  return {
    key: "outform-sys",
    title: "Outform — Container View",
    subtitle: "C4 Level 2 · v3.0 · 2 Sept 2026 · measured, not remembered",
    cols: [
      { root: box("os-actors", undefined, "col", [person("os-estim", "Estimator"), person("os-agent", "The agent")], { pad: 0, gap: 40 }) },
      { root: frontend, align: "center" },
      { root: backend, align: "top" },
      { root: db, align: "top" },
    ],
    wires: [
      { a: "os-estim", b: "os-shell", as: "r", bs: "l", label: "HTTPS" },
      { a: "os-agent", b: "os-chat", as: "r", bs: "l", label: "omnigent" },
      { a: "os-bp", b: "os-api", as: "r", bs: "l", label: "REST" },
      { a: "os-chat", b: "os-tools", as: "r", bs: "l", label: "MCP" },
      { a: "os-tools", b: "os-writes", as: "l", bs: "l", label: "same door", red: true },
      { a: "os-frame", b: "os-views", as: "r", bs: "l", label: "HTML" },
      { a: "os-load", b: "os-cell", as: "r", bs: "l", label: "SQL" },
      { a: "os-prom", b: "os-bom", as: "r", bs: "l", label: "SQL" },
      { a: "os-est", b: "os-rate", as: "r", bs: "l", label: "facts in", red: true },
      { a: "os-engines", b: "os-estt", as: "r", bs: "l", label: "no inputs", dashed: true },
      { a: "os-est", b: "os-estt", as: "r", bs: "l", label: "price NOT written back", dashed: true, red: true },
      { a: "os-writes", b: "os-fe2", as: "r", bs: "l", label: "SQL", red: true },
      { a: "os-rev", b: "os-api", as: "r", bs: "l", label: "/review-queue — no caller", dashed: true },
      { a: "os-repo", b: "os-db", as: "r", bs: "l", label: "bypassed", dashed: true },
    ],
    legend: [
      { style: "solid", label: "solid = working today" },
      { style: "dash", label: "dashed = the wire that is missing" },
      { style: "red", label: "red = business logic, or the write door" },
    ],
  };
})();

export function OutformArchitectureDiagram() {
  return (
    <div style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
      <div style={{ fontSize: 12.5, color: "#5B6B7C", marginBottom: 22, maxWidth: 1080, lineHeight: 1.6 }}>
        The system by component, measured on 2 September rather than remembered. Two things are red because they are
        the two a nine-cycle audit found already right: the <strong style={{ color: "#2F3E4F" }}>pure kernel</strong>,
        1,442 lines with zero I/O imports, and the <strong style={{ color: "#2F3E4F" }}>write gate</strong>, the single
        door a fact can change through — the same door the agent uses, which is why "can the agent enter this?" is a
        question with an answer. <strong style={{ color: "#2F3E4F" }}>Every dashed wire is a defect</strong>, and each
        one is a missing connection rather than a wrong shape: the price is computed and never written back, the
        analogous method has no inputs because of it, the review queue has an endpoint and no screen, and the
        repository layer does not exist so fifteen modules reach past it into Postgres.
      </div>
      <div style={HEAD}>Where the business logic lives — and where the wires are missing</div>
      <Canvas d={SYS} mode="contrast" />
    </div>
  );
}
